import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [settingsData, setSettingsData] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [defaultTokensInput, setDefaultTokensInput] = useState(20);
  const [bookingRewardTokensInput, setBookingRewardTokensInput] = useState(20);
  const [savingTokens, setSavingTokens] = useState(false);

  useEffect(() => {
    fetchSettingsData();
  }, []);

  const fetchSettingsData = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getAdminSettings();
      if (response.success) {
        setSettingsData(response);
        if (response.tokenSettings) {
          if (response.tokenSettings.defaultTokenAllocation !== undefined) {
            setDefaultTokensInput(response.tokenSettings.defaultTokenAllocation);
          }
          if (response.tokenSettings.bookingRewardTokens !== undefined) {
            setBookingRewardTokensInput(response.tokenSettings.bookingRewardTokens);
          }
        }
      } else {
        toast.error(response.message || 'Failed to fetch settings data');
      }
    } catch (error) {
      console.error('Error fetching settings data:', error);
      toast.error('Failed to fetch settings data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDefaultTokens = async (e) => {
    e.preventDefault();
    const defaultVal = parseInt(defaultTokensInput);
    const rewardVal = parseInt(bookingRewardTokensInput);

    if (isNaN(defaultVal) || defaultVal < 0) {
      toast.error('Please enter a valid number of default tokens (>= 0)');
      return;
    }
    if (isNaN(rewardVal) || rewardVal < 0) {
      toast.error('Please enter a valid number of booking reward tokens (>= 0)');
      return;
    }

    try {
      setSavingTokens(true);
      const res = await adminApiService.updateTokenSettings({ 
        defaultTokenAllocation: defaultVal,
        bookingRewardTokens: rewardVal
      });
      if (res.success) {
        toast.success(`Token Settings updated: Default = ${defaultVal}, Reward = ${rewardVal}`);
        setShowTokenModal(false);
        await fetchSettingsData();
      } else {
        toast.error(res.message || 'Failed to update token settings');
      }
    } catch (err) {
      console.error('Error updating token settings:', err);
      toast.error('Failed to update token settings');
    } finally {
      setSavingTokens(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Settings</h1>
        <p className="text-gray-600 mt-2">Manage system configuration and settings</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        {/* API Manager Card */}
        <Link 
          to="/admin/dashboard/settings/api-manager" 
          className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-blue-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">API Manager</h3>
              <p className="text-gray-600 text-sm">Configure and manage API endpoints</p>
            </div>
          </div>
          {settingsData?.apiStatus && (
            <div className="text-sm text-gray-500">
              <span className="font-medium">APIs Configured: </span>
              {settingsData.apiStatus.apiInfo?.length || 0}
            </div>
          )}
        </Link>

        {/* Platform Settings Card */}
        <Link 
          to="/admin/dashboard/settings/platform" 
          className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-green-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-green-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Platform Fee & GST Settings</h3>
              <p className="text-gray-600 text-sm">Configure platform fees and GST rates</p>
            </div>
          </div>
          {settingsData?.platformSettings && (
            <div className="text-sm text-gray-500">
              <span className="font-medium">Platform Fee: </span>
              ₹{settingsData.platformSettings.platformFee || 0} | 
              <span className="font-medium ml-2">GST: </span>
              {settingsData.platformSettings.gstRate || 0}%
            </div>
          )}
        </Link>

        {/* Cancellation Policy Card */}
        <Link 
          to="/admin/dashboard/settings/cancellation-policy" 
          className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-orange-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-orange-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Cancellation Policy Settings</h3>
              <p className="text-gray-600 text-sm">Configure cancellation time limits</p>
            </div>
          </div>
          {settingsData?.cancellationSettings && (
            <div className="text-sm text-gray-500">
              <span className="font-medium">Cancellation Offset: </span>
              {settingsData.cancellationSettings.cancellationHoursOffset || 0} hours
            </div>
          )}
        </Link>

        {/* Default & Reward Token Settings Card */}
        <div 
          onClick={() => setShowTokenModal(true)}
          className="cursor-pointer bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-blue-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <i className="fas fa-coins text-blue-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Token Management Settings</h3>
              <p className="text-gray-600 text-sm">Configure approval allocation & booking reward tokens</p>
            </div>
          </div>
          <div className="text-sm text-gray-500 space-y-1">
            <div className="flex items-center justify-between">
              <span>Default Tokens on Approval:</span>
              <span className="text-blue-600 font-bold ml-1">
                {settingsData?.tokenSettings?.defaultTokenAllocation ?? 20} tokens
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Reward Tokens per Booking:</span>
              <span className="text-green-600 font-bold ml-1">
                {settingsData?.tokenSettings?.bookingRewardTokens ?? 20} tokens
              </span>
            </div>
          </div>
        </div>

          <Link 
          to="/admin/dashboard/settings/add-support-executive" 
          className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-purple-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-purple-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Support Executive</h3>
              <p className="text-gray-600 text-sm">Create a new support executive account</p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            <span className="font-medium">Register support team members</span>
          </div>
        </Link>

        {/* System Schedulers Card */}
        <Link 
          to="/admin/dashboard/settings/schedulers"
          className="block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 hover:border-red-300"
        >
          <div className="flex items-center mb-4">
            <div className="bg-red-100 p-3 rounded-lg">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">System Schedulers & Live Progress</h3>
              <p className="text-gray-600 text-sm">Monitor live execution progress, percentages, and background sync status</p>
            </div>
          </div>
          <div className="text-sm text-gray-500 flex justify-between items-center">
            <span className="font-medium">View live progress bars & trigger schedulers</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-800">
              Live Metrics Active
            </span>
          </div>
        </Link>
      </div>

      {/* Quick Stats Section */}
      {settingsData && (
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {settingsData.apiStatus?.apiInfo?.length || 0}
              </div>
              <div className="text-sm text-blue-800">Configured APIs</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                ₹{settingsData.platformSettings?.platformFee || 0}
              </div>
              <div className="text-sm text-green-800">Platform Fee</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {settingsData.cancellationSettings?.cancellationHoursOffset || 0}h
              </div>
              <div className="text-sm text-orange-800">Cancellation Window</div>
            </div>
          </div>
        </div>
      )}

      {/* Default & Reward Token Modal */}
      {showTokenModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
            <div className="flex justify-between items-center mb-4 border-b pb-3">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <i className="fas fa-coins text-blue-600 mr-2"></i>
                Configure Token Settings
              </h3>
              <button
                onClick={() => setShowTokenModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            <form onSubmit={handleSaveDefaultTokens} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Tokens Credited on Agent Approval *
                </label>
                <input
                  type="number"
                  min="0"
                  max="10000"
                  required
                  value={defaultTokensInput}
                  onChange={(e) => setDefaultTokensInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-medium text-gray-900"
                  placeholder="e.g. 20"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Credited to an agent's token account automatically upon approval.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Tokens Credited per Completed Booking *
                </label>
                <input
                  type="number"
                  min="0"
                  max="10000"
                  required
                  value={bookingRewardTokensInput}
                  onChange={(e) => setBookingRewardTokensInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm font-medium text-gray-900"
                  placeholder="e.g. 20"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Credited to an agent's token account whenever a hotel voucher booking completes successfully.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowTokenModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingTokens}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center"
                >
                  {savingTokens ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;